import { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { shouldClearSeenOnEyeClick } from "../utils/validation";
import { firebaseOptimizer, isTabVisible } from "../services/firebaseOptimizer";
import { logActivity, ACTION_TYPES } from "../utils/historyLogger";

// Converts a Firestore doc snapshot into a plain inventory item object.
// Defined outside the hook so it never changes reference between renders.
const buildInventoryItem = (docId, data) => {
  const quantity = Number(data.quantity) || 0;
  const hasSerial = data.serialNumber && data.serialNumber.trim() !== "";
  return {
    id: docId,
    assignments: data.assignments || { first: {}, second: {} },
    createdAt: data.createdAt,
    createdBy: data.createdBy || "",
    currentUnitId: data.currentUnitId || "",
    isOrganic: Boolean(data.isOrganic),
    itemDefinitionId: data.itemDefinitionId || "",
    itemName: data.itemName || "",
    quantity,
    serialNumber: data.serialNumber || "",
    status: data.status || "available",
    ...(hasSerial && data.seenAt         ? { seenAt: data.seenAt }               : {}),
    ...(hasSerial && data.previousSeenAt ? { previousSeenAt: data.previousSeenAt } : {}),
    ...(hasSerial ? {} : {
      availableQuantity: data.availableQuantity !== undefined
        ? Number(data.availableQuantity)
        : quantity,
    }),
    updatedAt: data.updatedAt,
  };
};

// Patches a single item in an array by id without mapping over the whole list.
const patchItemInArray = (prev, id, changes) => {
  const idx = prev.findIndex((i) => i.id === id);
  if (idx === -1) return prev;
  const next = [...prev];
  next[idx] = { ...next[idx], ...changes };
  return next;
};

/**
 * Custom hook for inventory management operations
 * Complex database structure with assignments, status tracking, and relationships
 */
export const useInventory = () => {
  const [inventory, setInventory] = useState([]);
  const inventoryRef = useRef(inventory);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    inventoryRef.current = inventory;
  }, [inventory]);

  // Helper function to convert Firestore timestamps to readable dates
  const formatFirestoreDate = useCallback((timestamp) => {
    if (!timestamp) return "לא זמין";
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("he-IL");
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString("he-IL");
    }
    try {
      return new Date(timestamp).toLocaleString("he-IL");
    } catch (error) {
      return "לא זמין";
    }
  }, []);

  // Optimized inventory listener with smart caching
  useEffect(() => {
    // Check cache first with timestamp validation
    const cachedData = firebaseOptimizer.getCachedData("inventory");
    const cacheTimestamp = firebaseOptimizer.getCacheTimestamp("inventory");
    const now = Date.now();
    const cacheValid = cacheTimestamp && now - cacheTimestamp < 300000; // 5 minutes

    if (cachedData && cacheValid) {
      setInventory(cachedData);
      setLoading(false);
      return;
    }

    // Skip if tab not visible
    if (!isTabVisible()) {
      setLoading(false);
      return;
    }

    // Load from Firebase with optimized query
    const inventoryRef = collection(db, "inventory");
    const q = query(inventoryRef, orderBy("createdAt", "desc"));

    // Use connection management to prevent duplicate listeners
    const unsubscribe = firebaseOptimizer.getListener(
      "inventory",
      q,
      (snapshot) => {
        firebaseOptimizer.trackRead(snapshot.docs.length);
        const changes = snapshot.docChanges();

        // Initial load: every change is 'added' — build full array once
        if (changes.every((c) => c.type === "added")) {
          const inventoryData = changes.map((c) =>
            buildInventoryItem(c.doc.id, c.doc.data())
          );
          setInventory(inventoryData);
          firebaseOptimizer.setCachedData("inventory", inventoryData);
          firebaseOptimizer.setCacheTimestamp("inventory", now);
        } else {
          // Subsequent updates: patch only the changed items — O(changed) not O(n)
          setInventory((prev) => {
            const next = [...prev];
            for (const change of changes) {
              const idx = next.findIndex((i) => i.id === change.doc.id);
              if (change.type === "removed") {
                if (idx >= 0) next.splice(idx, 1);
              } else {
                const item = buildInventoryItem(change.doc.id, change.doc.data());
                if (idx >= 0) next[idx] = item;
                else next.unshift(item);
              }
            }
            return next;
          });
          firebaseOptimizer.clearCache("inventory");
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Add inventory item with optimized performance
  const addInventoryItem = async (itemData) => {
    try {
      const newItemRef = doc(collection(db, "inventory"));

      // For non-serial items, add availableQuantity field (starts equal to quantity)
      const hasSerialNumber =
        itemData.serialNumber && itemData.serialNumber.trim() !== "";
      const newItem = {
        ...itemData,
        assignments: { first: {}, second: {} }, // Changed from arrays to objects
        // For non-serial items, set availableQuantity equal to quantity
        ...(hasSerialNumber
          ? {}
          : { availableQuantity: itemData.quantity || 0 }),
        createdAt: new Date(),
        createdBy: itemData.createdBy || "system",
        updatedAt: new Date(),
      };
      await setDoc(newItemRef, newItem);

      // Immediately update local state for instant UI feedback
      const newItemWithId = {
        id: newItemRef.id,
        ...newItem,
      };
      setInventory((prev) => [...prev, newItemWithId]);

      // Update cache with new item
      const cachedData = firebaseOptimizer.getCachedData("inventory") || [];
      firebaseOptimizer.setCachedData("inventory", [
        ...cachedData,
        newItemWithId,
      ]);
      firebaseOptimizer.setCacheTimestamp("inventory", Date.now());

      // Log activity
      await logActivity({
        actionType: ACTION_TYPES.CREATED,
        itemId: newItemRef.id,
        itemName: itemData.itemName || "פריט ללא שם",
        serialNumber: itemData.serialNumber || null,
        details: {
          quantity: itemData.quantity || 0,
          isOrganic: itemData.isOrganic || false,
        },
      });

      return { success: true };
    } catch (e) {
      console.error("Error adding inventory item: ", e);
      return { success: false, error: e.message };
    }
  };

  // Update inventory item with optimized performance
  const updateInventoryItem = async (id, updatedData) => {
    try {
      const itemRef = doc(db, "inventory", id);
      const item = inventory.find((item) => item.id === id);

      if (!item) {
        return { success: false, error: "Item not found" };
      }

      // Detect all changes
      const changes = {};

      // Check quantity change
      if (
        updatedData.quantity !== undefined &&
        updatedData.quantity !== item.quantity
      ) {
        changes.quantity = {
          old: item.quantity || 0,
          new: updatedData.quantity || 0,
        };
      }

      // Check serial number change
      if (
        updatedData.serialNumber !== undefined &&
        updatedData.serialNumber !== item.serialNumber
      ) {
        changes.serialNumber = {
          old: item.serialNumber || "",
          new: updatedData.serialNumber || "",
        };
      }

      // Check type (isOrganic) change
      if (
        updatedData.isOrganic !== undefined &&
        updatedData.isOrganic !== item.isOrganic
      ) {
        changes.isOrganic = {
          old: item.isOrganic || false,
          new: updatedData.isOrganic || false,
        };
      }

      // Check item name change
      if (
        updatedData.itemName !== undefined &&
        updatedData.itemName !== item.itemName
      ) {
        changes.itemName = {
          old: item.itemName || "",
          new: updatedData.itemName || "",
        };
      }

      // Check itemDefinitionId change
      if (
        updatedData.itemDefinitionId !== undefined &&
        updatedData.itemDefinitionId !== item.itemDefinitionId
      ) {
        changes.itemDefinitionId = {
          old: item.itemDefinitionId || "",
          new: updatedData.itemDefinitionId || "",
        };
      }

      const updateData = { ...updatedData, updatedAt: new Date() };
      await updateDoc(itemRef, updateData);

      // Immediately update local state for instant UI feedback
      setInventory((prev) => patchItemInArray(prev, id, updateData));

      // Update cache with modified item
      const cachedData = firebaseOptimizer.getCachedData("inventory") || [];
      firebaseOptimizer.setCachedData("inventory", patchItemInArray(cachedData, id, updateData));
      firebaseOptimizer.setCacheTimestamp("inventory", Date.now());

      // Log activity - use AMOUNT_CHANGED if only quantity changed, otherwise UPDATED
      const hasChanges = Object.keys(changes).length > 0;
      if (hasChanges) {
        const actionType =
          Object.keys(changes).length === 1 && changes.quantity
            ? ACTION_TYPES.AMOUNT_CHANGED
            : ACTION_TYPES.UPDATED;

        await logActivity({
          actionType,
          itemId: id,
          itemName: updatedData.itemName || item.itemName || "פריט ללא שם",
          serialNumber: updatedData.serialNumber || item.serialNumber || null,
          details: {
            changes,
            // Keep old format for backward compatibility
            ...(changes.quantity && {
              oldValue: changes.quantity.old,
              newValue: changes.quantity.new,
            }),
          },
        });
      }

      return { success: true };
    } catch (e) {
      console.error("Error updating inventory item: ", e);
      return { success: false, error: e.message };
    }
  };

  // Assign item to unit
  const assignItem = async (id, unitId, userId) => {
    try {
      const itemRef = doc(db, "inventory", id);
      const item = inventory.find((item) => item.id === id);

      const updateData = {
        currentUnitId: unitId,
        status: "assigned",
        updatedAt: new Date(),
      };
      await updateDoc(itemRef, updateData);

      // Immediately update local state for instant UI feedback
      setInventory((prev) => patchItemInArray(prev, id, updateData));

      firebaseOptimizer.clearCache("inventory");

      // Log activity
      if (item) {
        await logActivity({
          actionType: ACTION_TYPES.ASSIGNED,
          itemId: id,
          itemName: item.itemName || "פריט ללא שם",
          serialNumber: item.serialNumber || null,
          details: {
            unitId,
            quantity: item.quantity || 1,
          },
        });
      }

      return { success: true };
    } catch (e) {
      console.error("Error assigning item: ", e);
      return { success: false, error: e.message };
    }
  };

  // Return item (change status to returned and hide from inventory)
  const returnItem = async (id, quantity = null) => {
    try {
      const itemRef = doc(db, "inventory", id);
      const item = inventory.find((item) => item.id === id);

      if (!item) {
        return { success: false, error: "Item not found" };
      }

      // For serial items, mark as returned
      if (item.serialNumber) {
        const updateData = {
          status: "returned",
          currentUnitId: "",
          updatedAt: new Date(),
        };
        await updateDoc(itemRef, updateData);

        // Immediately update local state
        setInventory((prev) => patchItemInArray(prev, id, updateData));
        firebaseOptimizer.clearCache("inventory");

        // Log activity
        await logActivity({
          actionType: ACTION_TYPES.RETURNED,
          itemId: id,
          itemName: item.itemName || "פריט ללא שם",
          serialNumber: item.serialNumber || null,
          details: {
            quantity: 1, // Serial items are always quantity 1
          },
        });

        return { success: true };
      }

      // For non-serial items, reduce availableQuantity
      if (!item.serialNumber && quantity) {
        const currentAvailableQuantity =
          item.availableQuantity ?? item.quantity ?? 0;

        // Validate that we're not returning more than available
        if (quantity > currentAvailableQuantity) {
          return {
            success: false,
            error: `Cannot return more than available (${currentAvailableQuantity})`,
          };
        }

        const newAvailableQuantity = currentAvailableQuantity - quantity;

        let updateData = {
          availableQuantity: newAvailableQuantity,
          updatedAt: new Date(),
        };

        // If all available quantity is returned, mark as returned
        if (newAvailableQuantity <= 0) {
          updateData.status = "returned";
          updateData.currentUnitId = "";
        } else {
          // Still has available quantity, keep status as available
          updateData.status = "available";
        }

        await updateDoc(itemRef, updateData);

        // Immediately update local state
        setInventory((prev) => patchItemInArray(prev, id, updateData));
        firebaseOptimizer.clearCache("inventory");

        // Log activity
        await logActivity({
          actionType: ACTION_TYPES.RETURNED,
          itemId: id,
          itemName: item.itemName || "פריט ללא שם",
          serialNumber: item.serialNumber || null,
          details: {
            quantity,
            oldAvailableQuantity: currentAvailableQuantity,
            newAvailableQuantity: newAvailableQuantity,
          },
        });

        return { success: true };
      }

      // Default: mark as returned (for serial items without quantity parameter)
      const updateData = {
        status: "returned",
        currentUnitId: "",
        updatedAt: new Date(),
      };
      await updateDoc(itemRef, updateData);

      // Immediately update local state for instant UI feedback
      setInventory((prev) => patchItemInArray(prev, id, updateData));

      firebaseOptimizer.clearCache("inventory");

      // Log activity
      await logActivity({
        actionType: ACTION_TYPES.RETURNED,
        itemId: id,
        itemName: item.itemName || "פריט ללא שם",
        serialNumber: item.serialNumber || null,
        details: {},
      });

      return { success: true };
    } catch (e) {
      console.error("Error returning item: ", e);
      return { success: false, error: e.message };
    }
  };

  // Delete inventory item with optimized performance
  const deleteInventoryItem = async (id) => {
    try {
      const item = inventory.find((item) => item.id === id);

      await deleteDoc(doc(db, "inventory", id));

      // Immediately update local state for instant UI feedback
      setInventory((prev) => prev.filter((item) => item.id !== id));

      // Update cache by removing deleted item
      const cachedData = firebaseOptimizer.getCachedData("inventory") || [];
      const updatedCache = cachedData.filter((item) => item.id !== id);
      firebaseOptimizer.setCachedData("inventory", updatedCache);
      firebaseOptimizer.setCacheTimestamp("inventory", Date.now());

      // Log activity
      if (item) {
        await logActivity({
          actionType: ACTION_TYPES.DELETED,
          itemId: id,
          itemName: item.itemName || "פריט ללא שם",
          serialNumber: item.serialNumber || null,
          details: {
            quantity: item.quantity || 0,
          },
        });
      }

      return { success: true };
    } catch (e) {
      console.error("Error deleting inventory item: ", e);
      return { success: false, error: e.message };
    }
  };

  // Get available items
  const getAvailableItems = useCallback(() => {
    return inventory.filter((item) => item.status === "available");
  }, [inventory]);

  // Get assigned items
  const getAssignedItems = useCallback(() => {
    return inventory.filter((item) => item.status === "assigned");
  }, [inventory]);

  // Get returned items
  const getReturnedItems = useCallback(() => {
    return inventory.filter((item) => item.status === "returned");
  }, [inventory]);

  // Make item available again (from returned to available)
  const makeItemAvailable = async (id) => {
    try {
      const itemRef = doc(db, "inventory", id);
      const updateData = {
        status: "available",
        updatedAt: new Date(),
      };
      await updateDoc(itemRef, updateData);

      // Immediately update local state for instant UI feedback
      setInventory((prev) => patchItemInArray(prev, id, updateData));

      // Update cache with modified item
      const cachedData = firebaseOptimizer.getCachedData("inventory") || [];
      firebaseOptimizer.setCachedData("inventory", patchItemInArray(cachedData, id, updateData));
      firebaseOptimizer.setCacheTimestamp("inventory", Date.now());

      // Log activity
      const item = inventory.find((item) => item.id === id);
      if (item) {
        await logActivity({
          actionType: ACTION_TYPES.MADE_AVAILABLE,
          itemId: id,
          itemName: item.itemName || "פריט ללא שם",
          serialNumber: item.serialNumber || null,
          details: {},
        });
      }

      return { success: true };
    } catch (e) {
      console.error("Error making item available: ", e);
      return { success: false, error: e.message };
    }
  };

  // Stable ref so Inventory row components are not recreated every inventory snapshot (avoids scroll jump)
  const updateSeenAt = useCallback(async (id, opts = {}) => {
    const greenMax = opts.greenMax ?? 2;
    const yellowDays = opts.yellowDays ?? 3;
    const inv = inventoryRef.current;
    const item = inv.find((i) => i.id === id);
    const seenAt =
      opts.seenAt !== undefined ? opts.seenAt : item?.seenAt;
    const previousSeenAt = item?.previousSeenAt ?? null;
    const shouldClear = shouldClearSeenOnEyeClick(
      seenAt,
      greenMax,
      yellowDays,
    );

    try {
      const itemRef = doc(db, "inventory", id);
      const now = new Date();

      let updateData;
      if (shouldClear) {
        if (previousSeenAt) {
          // Restore the date that existed before the first click
          updateData = { seenAt: previousSeenAt, previousSeenAt: deleteField(), updatedAt: now };
        } else {
          // No prior date to restore — clear as before
          updateData = { seenAt: deleteField(), updatedAt: now };
        }
      } else {
        // Marking as seen: backup current seenAt so it can be restored on re-click
        updateData = seenAt
          ? { previousSeenAt: seenAt, seenAt: now, updatedAt: now }
          : { seenAt: now, updatedAt: now };
      }

      await updateDoc(itemRef, updateData);

      setInventory((prev) => {
        const itemIndex = prev.findIndex((row) => row.id === id);
        if (itemIndex === -1) return prev;

        const updated = [...prev];
        if (shouldClear) {
          const next = { ...updated[itemIndex], updatedAt: now };
          delete next.previousSeenAt;
          if (previousSeenAt) {
            next.seenAt = previousSeenAt;
          } else {
            delete next.seenAt;
          }
          updated[itemIndex] = next;
        } else {
          updated[itemIndex] = {
            ...updated[itemIndex],
            ...(seenAt ? { previousSeenAt: seenAt } : {}),
            seenAt: now,
            updatedAt: now,
          };
        }
        return updated;
      });

      const cachedData = firebaseOptimizer.getCachedData("inventory");
      if (cachedData && Array.isArray(cachedData)) {
        const cacheIndex = cachedData.findIndex((row) => row.id === id);
        if (cacheIndex !== -1) {
          const updatedCache = [...cachedData];
          if (shouldClear) {
            const next = { ...updatedCache[cacheIndex], updatedAt: now };
            delete next.previousSeenAt;
            if (previousSeenAt) {
              next.seenAt = previousSeenAt;
            } else {
              delete next.seenAt;
            }
            updatedCache[cacheIndex] = next;
          } else {
            updatedCache[cacheIndex] = {
              ...updatedCache[cacheIndex],
              ...(seenAt ? { previousSeenAt: seenAt } : {}),
              seenAt: now,
              updatedAt: now,
            };
          }
          firebaseOptimizer.setCachedData("inventory", updatedCache);
          firebaseOptimizer.setCacheTimestamp("inventory", Date.now());
        }
      }

      if (item) {
        try {
          await logActivity({
            actionType: ACTION_TYPES.SEEN,
            itemId: id,
            itemName: item.itemName || "פריט ללא שם",
            serialNumber: item.serialNumber || null,
            details: shouldClear
              ? { clearedSeenAt: true, restoredSeenAt: previousSeenAt || null }
              : { seenAt: now },
          });
        } catch (logErr) {
          if (process.env.NODE_ENV === "development") {
            console.warn("seenAt saved but history log failed:", logErr);
          }
        }
      }

      return { success: true };
    } catch (e) {
      console.error("Error updating seenAt: ", e);
      return { success: false, error: e.message };
    }
  }, []);

  const updateAssignmentNote = useCallback(
    async (itemId, personalNumber, level, note) => {
      try {
        const itemRef = doc(db, "inventory", itemId);
        await updateDoc(itemRef, {
          [`assignments.${level}.${personalNumber}.note`]: note,
          updatedAt: serverTimestamp(),
        });
        return { success: true };
      } catch (e) {
        console.error("Error updating assignment note: ", e);
        return { success: false, error: e.message };
      }
    },
    []
  );

  const refreshInventory = useCallback(async () => {
    firebaseOptimizer.clearCache("inventory");
    const inventoryRef = collection(db, "inventory");
    const q = query(inventoryRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    const inventoryData = snapshot.docs.map((docSnap) =>
      buildInventoryItem(docSnap.id, docSnap.data())
    );

    setInventory(inventoryData);
    firebaseOptimizer.setCachedData("inventory", inventoryData);
    firebaseOptimizer.setCacheTimestamp("inventory", Date.now());
  }, []);

  return {
    inventory,
    loading,
    addInventoryItem,
    updateInventoryItem,
    assignItem,
    returnItem,
    makeItemAvailable,
    deleteInventoryItem,
    updateSeenAt,
    formatFirestoreDate,
    getAvailableItems,
    getAssignedItems,
    getReturnedItems,
    refreshInventory,
    updateAssignmentNote,
  };
};
